import React, { useState, useMemo, useCallback } from "react"
import ButtonTooltip from "components/Common/ButtonTooltip"
import StatusBadge from "components/Common/StatusBadge"
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import VehicleStockContentAddEditModal from "pages/ContentManagerPages/PostStockList/VehicleStockContentAddEditModal"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import TableLocal from "components/Common/TableLocal/TableLocal"
import TableLocalPagination from "components/Common/TableLocal/TableLocalPagination"

const VehicleContentsModal = ({ modal, toggle, selectedData, onContentChange }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)
  const [sortOrder, setSortOrder] = useState("desc")

  const isSinglePlatform = selectedData?.platform && selectedData?.contents

  const sortedContents = useMemo(() => {
    const contents = isSinglePlatform
      ? selectedData?.contents || []
      : selectedData?.publishes?.flatMap(pub =>
          (pub?.contents || []).map(c => ({ ...c, platform: pub?.platform }))
        ) || []

    return [...contents].sort((a, b) => {
      const dateA = new Date(a.date)
      const dateB = new Date(b.date)
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB
    })
  }, [selectedData, sortOrder, isSinglePlatform])

  const toggleSortOrder = useCallback(() => {
    setSortOrder(prev => (prev === "desc" ? "asc" : "desc"))
    setCurrentPage(1)
  }, [])

  const columns = useMemo(
    () => [
      ...(isSinglePlatform
        ? []
        : [
            {
              header: "Platform",
              cell: row => <span>{row?.platform?.name || "-"}</span>,
            },
          ]),
      {
        header: "ID",
        accessor: "id",
      },
      {
        header: "Content Link",
        cell: row =>
          row?.url ? (
            <ButtonTooltip
              type="view"
              btnId={`content-link-${row?.id}`}
              onClick={() => window.open(row.url, "_blank")}
            />
          ) : (
            "-"
          ),
      },
      {
        header: "Remarks",
        accessor: "remarks",
      },
      {
        header: "Published",
        cell: row => (
          <StatusBadge
            value={row.is_published}
            trueLabel="Yes"
            falseLabel="No"
          />
        ),
      },
      {
        header: (
          <div
            className="d-flex align-items-center gap-1 cursor-pointer"
            onClick={toggleSortOrder}
          >
            <span>Date</span>
            <i className={`mdi mdi-arrow-${sortOrder === "desc" ? "down" : "up"}`} />
          </div>
        ),
        cell: row => <span>{formatReadableDate(row.date)}</span>,
      },
      {
        header: "Action",
        cell: row => (
          <VehicleStockContentAddEditModal
            currentContent={row}
            isLabel={false}
            data={selectedData}
            onSuccess={onContentChange}
          />
        ),
      },
    ],
    [sortOrder, toggleSortOrder, isSinglePlatform, selectedData, onContentChange]
  )

  if (!selectedData) return null

  return (
    <Modal isOpen={modal} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        Vehicle Stock ID: {selectedData.stock_id || selectedData.vehicle_stock_id || selectedData?.id}
      </ModalHeader>
      <ModalBody>
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <strong>Platform:</strong> {selectedData.platform?.name ?? "-"}
            <span className="ms-3 text-muted">
              <i
                className={`mdi mdi-sort-${
                  sortOrder === "desc" ? "descending" : "ascending"
                } me-1`}
              />
              Sorted by date (
              {sortOrder === "desc" ? "newest first" : "oldest first"})
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              color="outline-secondary"
              size="sm"
              onClick={toggleSortOrder}
              className="d-flex align-items-center"
            >
              <i
                className={`mdi mdi-sort-${
                  sortOrder === "desc" ? "descending" : "ascending"
                } me-1`}
              />
              {sortOrder === "desc" ? "Newest First" : "Oldest First"}
            </Button>

            {isSinglePlatform && (
              <CanView access={[ACCESS.VEHICLE_CONTENT.ADD_VEHICLE_CONTENT]}>
                <VehicleStockContentAddEditModal
                  buttonLabel="Add Content"
                  data={selectedData}
                />
              </CanView>
            )}
          </div>
        </div>

        <TableLocal
          columns={columns}
          data={sortedContents}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          tableClass="align-middle"
          emptyMessage="No content available"
        />

        <TableLocalPagination
          data={sortedContents}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          showItemsPerPage={true}
          itemsPerPageOptions={[5, 10, 20]}
        />
      </ModalBody>
    </Modal>
  )
}

VehicleContentsModal.displayName = "VehicleContentsModal"

export default React.memo(VehicleContentsModal)